import { createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect, type AnyRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { bootstrapSession, homePath } from '@features/auth/model/session';
import { sessionStore } from '@shared/session/store';
import { DashboardGate } from './guards';
import { AppShell } from './layouts/AppShell';
import { AuthLayout } from './layouts/AuthLayout';
import { NotFoundPage, PendingPage, RootLayout, RouteErrorPage } from './layouts/RootLayout';

/*
 * Code-based TanStack Router tree. Every feature page is a lazy chunk; layouts and guards are eager.
 * Dashboards: /app/student, /app/teacher, /app/guardian, /app/admin, and the super-admin console
 * at /app/platform/* (codebase doc §11; /app/super-admin redirects there).
 */

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});

// "/" is the public landing page; signed-in users go straight to their dashboard.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    await bootstrapSession();
    if (sessionStore.get().status === 'authenticated') throw redirect({ to: homePath() });
  },
  component: lazyRouteComponent(() => import('@features/landing/pages/LandingPage')),
});

// The activation-code share message (enrollments/service.py) links to /activate.
const activateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'activate',
  beforeLoad: () => {
    throw redirect({ to: '/app/student/redeem' });
  },
});

// --- public auth screens ---------------------------------------------------------------------
const authLayout = createRoute({ getParentRoute: () => rootRoute, id: 'public', component: AuthLayout });

const loginRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'login',
  beforeLoad: async () => {
    await bootstrapSession();
    if (sessionStore.get().status === 'authenticated') throw redirect({ to: homePath() });
  },
  component: lazyRouteComponent(() => import('@features/auth/pages/LoginPage')),
});
const registerRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'register',
  component: lazyRouteComponent(() => import('@features/auth/pages/RegisterPage')),
});
const forgotRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'forgot-password',
  component: lazyRouteComponent(() => import('@features/auth/pages/ForgotPasswordPage')),
});
// Paths used in emailed links (apps/api/tibyan/auth/service.py).
const verifyEmailRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'auth/verify-email',
  component: lazyRouteComponent(() => import('@features/auth/pages/VerifyEmailPage')),
});
const acceptInviteRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'auth/accept-invite',
  component: lazyRouteComponent(() => import('@features/auth/pages/AcceptInvitePage')),
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'auth/reset-password',
  component: lazyRouteComponent(() => import('@features/auth/pages/ResetPasswordPage')),
});
// Public legal texts: /legal/terms, /legal/privacy, /legal/cookies, /legal/acceptable-use.
const legalIndexRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'legal',
  beforeLoad: () => {
    throw redirect({ to: '/legal/terms' });
  },
});
const legalRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'legal/$doc',
  component: lazyRouteComponent(() => import('@features/legal/pages/LegalPage')),
});

// --- authenticated app -----------------------------------------------------------------------
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'app',
  beforeLoad: async ({ location }) => {
    await bootstrapSession();
    if (sessionStore.get().status !== 'authenticated') {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppShell,
});

const appIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: homePath() });
  },
});

type PageModule = { default: () => ReactElement | null };
const page = (parent: AnyRoute, path: string, loader: () => Promise<PageModule>) =>
  createRoute({ getParentRoute: () => parent, path, component: lazyRouteComponent(loader) });

const gate = (dashboard: 'student' | 'teacher' | 'guardian' | 'admin' | 'super-admin', path: string = dashboard) =>
  createRoute({ getParentRoute: () => appRoute, path, component: () => <DashboardGate dashboard={dashboard} /> });

const redirectTo = (parent: AnyRoute, path: string, to: string) =>
  createRoute({
    getParentRoute: () => parent,
    path,
    beforeLoad: () => {
      throw redirect({ to });
    },
  });

const dashboardRoutes = {
  student: gate('student'),
  teacher: gate('teacher'),
  guardian: gate('guardian'),
  admin: gate('admin'),
  // Legacy /app/super-admin redirects to the platform console (/app/platform/*).
  superAdmin: gate('super-admin'),
  platform: gate('super-admin', 'platform'),
};

const studentRoutes = [
  page(dashboardRoutes.student, '/', () => import('@features/student/pages/StudentHomePage')),
  page(dashboardRoutes.student, 'calendar', () => import('@features/student/pages/StudentCalendarPage')),
  page(dashboardRoutes.student, 'learn', () => import('@features/student/pages/StudentLearnPage')),
  page(dashboardRoutes.student, 'progress', () => import('@features/progress/pages/ProgressPage')),
  page(dashboardRoutes.student, 'redeem', () => import('@features/finance/pages/RedeemCodePage')),
  page(dashboardRoutes.student, 'assignments', () => import('@features/assignments/pages/MyAssignmentsPage')),
  page(dashboardRoutes.student, 'assignments/$assignmentId', () => import('@features/assignments/pages/AssignmentDetailPage')),
  page(dashboardRoutes.student, 'exams', () => import('@features/exams/pages/StudentExamsPage')),
  page(dashboardRoutes.student, 'exams/$examId/take', () => import('@features/exams/pages/TakeExamPage')),
];
const teacherRoutes = [
  page(dashboardRoutes.teacher, '/', () => import('@features/teacher/pages/TeacherTodayPage')),
  page(dashboardRoutes.teacher, 'availability', () => import('@features/teacher/pages/TeacherAvailabilityPage')),
  page(dashboardRoutes.teacher, 'students', () => import('@features/teacher/pages/TeacherStudentsPage')),
  page(dashboardRoutes.teacher, 'teach', () => import('@features/teacher/pages/TeacherTeachPage')),
  page(dashboardRoutes.teacher, 'assignments', () => import('@features/assignments/pages/TeacherAssignmentsPage')),
  page(dashboardRoutes.teacher, 'exams', () => import('@features/exams/pages/TeacherExamsPage')),
  page(dashboardRoutes.teacher, 'gradebook', () => import('@features/exams/pages/GradebookPage')),
  page(dashboardRoutes.teacher, 'sessions/$occurrenceId/wrap-up', () => import('@features/sessions/pages/WrapUpPage')),
];
const guardianRoutes = [
  page(dashboardRoutes.guardian, '/', () => import('@features/guardian/pages/GuardianHomePage')),
  page(dashboardRoutes.guardian, 'calendar', () => import('@features/guardian/pages/GuardianCalendarPage')),
  page(dashboardRoutes.guardian, 'payments', () => import('@features/guardian/pages/GuardianPaymentsPage')),
  page(dashboardRoutes.guardian, 'assignments', () => import('@features/assignments/pages/GuardianAssignmentsPage')),
  page(dashboardRoutes.guardian, 'consents', () => import('@features/consents/pages/GuardianConsentsPage')),
  redirectTo(dashboardRoutes.guardian, 'messages', '/app/messages'),
];
const adminRoutes = [
  page(dashboardRoutes.admin, '/', () => import('@features/admin/pages/AdminDashboardPage')),
  page(dashboardRoutes.admin, 'members', () => import('@features/members/pages/MembersPage')),
  page(dashboardRoutes.admin, 'teachers', () => import('@features/admin/pages/AdminTeachersPage')),
  page(dashboardRoutes.admin, 'students', () => import('@features/admin/pages/AdminStudentsPage')),
  page(dashboardRoutes.admin, 'courses', () => import('@features/admin/pages/AdminCoursesPage')),
  page(dashboardRoutes.admin, 'enrollments', () => import('@features/admin/pages/AdminEnrollmentsPage')),
  page(dashboardRoutes.admin, 'finance/payments', () => import('@features/finance/pages/PaymentsPage')),
  page(dashboardRoutes.admin, 'finance/codes', () => import('@features/finance/pages/ActivationCodesPage')),
  page(dashboardRoutes.admin, 'settings', () => import('@features/settings/pages/OrgSettingsPage')),
  page(dashboardRoutes.admin, 'gradebook', () => import('@features/exams/pages/GradebookPage')),
  page(dashboardRoutes.admin, 'calendar', () => import('@features/calendar/pages/AdminCalendarPage')),
  page(dashboardRoutes.admin, 'support', () => import('@features/support/pages/TicketQueuePage')),
  page(dashboardRoutes.admin, 'support/$ticketId', () => import('@features/support/pages/StaffTicketPage')),
  page(dashboardRoutes.admin, 'moderation', () => import('@features/moderation/pages/ModerationQueuePage')),
  page(dashboardRoutes.admin, 'audit', () => import('@features/support/pages/AuditLogPage')),
  page(dashboardRoutes.admin, 'erasure', () => import('@features/support/pages/ErasureRequestsPage')),
];
const superAdminRoutes = [redirectTo(dashboardRoutes.superAdmin, '/', '/app/platform/organizations')];
const platformRoutes = [
  redirectTo(dashboardRoutes.platform, '/', '/app/platform/organizations'),
  page(dashboardRoutes.platform, 'organizations', () => import('@features/super-admin/pages/SuperAdminOrganizationsPage')),
  page(dashboardRoutes.platform, 'users', () => import('@features/super-admin/pages/SuperAdminUsersPage')),
  page(dashboardRoutes.platform, 'health', () => import('@features/super-admin/pages/SuperAdminHealthPage')),
  page(dashboardRoutes.platform, 'content', () => import('@features/super-admin/pages/ContentSourcesPage')),
];

const sharedAppRoutes = [
  page(appRoute, 'select-organization', () => import('@features/auth/pages/SelectOrganizationPage')),
  page(appRoute, 'class/$occurrenceId', () => import('@features/classroom/pages/ClassRoomPage')),
  page(appRoute, 'quran', () => import('@features/quran/pages/QuranViewerPage')),
  page(appRoute, 'messages', () => import('@features/messages/pages/MessagesPage')),
  page(appRoute, 'notifications', () => import('@features/notifications/pages/NotificationsPage')),
  page(appRoute, 'reschedule', () => import('@features/calendar/pages/RescheduleRequestsPage')),
  page(appRoute, 'announcements', () => import('@features/support/pages/AnnouncementsPage')),
  page(appRoute, 'help', () => import('@features/support/pages/HelpPage')),
  page(appRoute, 'help/tickets/$ticketId', () => import('@features/support/pages/MyTicketPage')),
  page(appRoute, 'help/articles/$slug', () => import('@features/help/pages/HelpArticlePage')),
  page(appRoute, 'account/notifications', () => import('@features/notifications/pages/NotificationPreferencesPage')),
  page(appRoute, 'enrollments/$enrollmentId/progress', () => import('@features/progress/pages/EnrollmentProgressPage')),
  page(appRoute, 'account/profile', () => import('@features/account/pages/ProfilePage')),
  page(appRoute, 'account/sessions', () => import('@features/account/pages/SessionsPage')),
  page(appRoute, 'account/security', () => import('@features/auth/pages/MfaSetupPage')),
  // Dev-only component gallery: not registered (and not bundled) in production builds, so /app/_ui is a 404 there.
  ...(import.meta.env.DEV ? [page(appRoute, '_ui', () => import('@features/dev-ui/pages/UiKitPage'))] : []),
];

const routeTree = rootRoute.addChildren([
  indexRoute,
  activateRoute,
  authLayout.addChildren([loginRoute, registerRoute, forgotRoute, verifyEmailRoute, acceptInviteRoute, resetPasswordRoute, legalIndexRoute, legalRoute]),
  appRoute.addChildren([
    appIndexRoute,
    ...sharedAppRoutes,
    dashboardRoutes.student.addChildren(studentRoutes),
    dashboardRoutes.teacher.addChildren(teacherRoutes),
    dashboardRoutes.guardian.addChildren(guardianRoutes),
    dashboardRoutes.admin.addChildren(adminRoutes),
    dashboardRoutes.superAdmin.addChildren(superAdminRoutes),
    dashboardRoutes.platform.addChildren(platformRoutes),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPendingComponent: PendingPage,
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  defaultErrorComponent: RouteErrorPage,
  defaultPreload: 'intent',
  scrollRestoration: true,
});
